import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BrandAdsReport } from '@/components/brands/BrandAdsReport';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { brandsApi } from '@/api/client';

export function BrandDashboardPage() {
  const { brandId: brandIdParam } = useParams<{ brandId: string }>();
  const brandId = brandIdParam ? decodeURIComponent(brandIdParam) : '';
  const { user } = useAuth();
  const isDemo = Boolean(user?.is_demo);
  const { range } = useAppDateRange();

  const brandQuery = useQuery({
    queryKey: ['brand', brandId],
    queryFn: async () => {
      const res = await brandsApi.get(brandId);
      if (!res.success || !res.data) throw new Error('Failed to load brand');
      return res.data.brand;
    },
    enabled: Boolean(brandId),
  });

  if (brandQuery.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-foreground-muted">
        <Spinner />
        Loading brand…
      </div>
    );
  }

  if (brandQuery.isError || !brandQuery.data) {
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-sm text-error-ink">Could not load this brand.</p>
        <Link to="/brands" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">
          Back to brands
        </Link>
      </div>
    );
  }

  return (
    <BrandAdsReport
      brandId={brandId}
      brandName={brandQuery.data.name}
      startDate={range.current.startDate}
      endDate={range.current.endDate}
      isDemo={isDemo}
    />
  );
}
